/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Field } from "formik";
import { Tooltip } from "@/shared";
import { Icons } from "@/shared";
import {
  InputContainer,
  Label,
  StyledInput,
  InputWrapper,
  HelpOverlay,
  HelpArea,
} from "./Input.styles";
import DateField from "./components/DateField";
import { InputDateProps } from "./components/DateField.types";
import { getCurrentYear } from "./components/date-utils";

export interface InputProps extends InputDateProps {
  id: string;
  name: string;
  label: string;
  type?: "text" | "email" | "password" | "number" | "date";
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"];
  helpText?: string;
  helpTooltipPosition?: "top" | "bottom" | "left" | "right" | "inputInfo";
}

export const Input: React.FC<InputProps> = ({
  id,
  name,
  label,
  type = "text",
  placeholder,
  disabled = false,
  className,
  autoComplete,
  helpText,
  helpTooltipPosition = "inputInfo",
  // Props específicas para fechas
  dateGranularity = "day",
  minYear = 1900,
  maxYear = getCurrentYear() + 10,
  maxDate,
  required = false,
  onDateChange,
}) => {
  if (type === "date") {
    return (
      <DateField
        id={id}
        name={name}
        label={label}
        disabled={disabled}
        className={className}
        helpText={helpText}
        helpTooltipPosition={helpTooltipPosition}
        dateGranularity={dateGranularity}
        minYear={minYear}
        maxYear={maxYear}
        maxDate={maxDate}
        required={required}
        autoComplete={autoComplete}
        onDateChange={onDateChange}
      />
    );
  }

  return (
      <InputContainer className={className}>
      <Label htmlFor={id} $required={required}>
        {label}
      </Label>

      <InputWrapper>
        <Field name={name}>
          {({ field }: any) => {
            let maxLen = undefined;
            let filterRegex: RegExp | null = null;

            if (name === "docNumber") {
              maxLen = 15;
              filterRegex = /[^a-zA-Z0-9]/g;
            } else if (name === "identificationNumber") {
              maxLen = 11;
              filterRegex = /\D/g;
            } else if (
              name === "name" ||
              name === "nameSurname" ||
              name === "otherNames" ||
              name === "lastName" ||
              name === "otherLastNames"
            ) {
              maxLen = name === "nameSurname" ? 50 : 30;
              filterRegex = /[^a-zA-Z\u00C0-\u017F\sñÑ'’]/g;
            }

            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              let value = e.target.value;

              if (filterRegex) {
                value = value.replace(filterRegex, "");
              }

              if (maxLen !== undefined) {
                value = value.slice(0, maxLen);
              }

              field.onChange({
                ...e,
                target: {
                  ...e.target,
                  value,
                  name: field.name,
                },
              });
            };

            return (
              <StyledInput
                {...field}
                id={id}
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete={autoComplete}
                $hasHelpIcon={!!helpText}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                  e.target.select();
                }}
                onChange={handleChange}
              />
            );
          }}
        </Field>
        {helpText ? (
          <HelpOverlay>
            <HelpArea aria-label="Ayuda" role="img">
              <Tooltip title={helpText} position={helpTooltipPosition}>
                <Icons name="iconInfo" width={24} height={24} />
              </Tooltip>
            </HelpArea>
          </HelpOverlay>
        ) : null}
      </InputWrapper>

    </InputContainer>
  );
};

export default Input;
